import React, { Dispatch, Fragment, useCallback, useMemo } from 'react';
import InputSkeleton from '../../../components/loaders/InputSkeleton';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import useModelSelectOptions from '../../../hooks/useModelSelectOptions';
import { customStyles } from '../../../utils/SelectStyles';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandAccordion from '../../../components/ExpandesAccordion';
import { TOptions } from '../../../types/types';
import { ICity } from '../../cities/types/interfaces';
import Tabs from '../../../components/Tabs';
import { homeDeliveryIcon, showroomIcon } from '../../../config/variables';

export default function CreateOrderFormDeliveryInputs({
  formik,
  expanded,
  setExpanded
}: {
  formik: FormikProps<any>;
  expanded: number;
  setExpanded: Dispatch<React.SetStateAction<number>>;
}) {
  const { t, i18n } = useTranslation();

  const deliveryOptions = [
    { value: 'home-delivery', label: t('home_delivery') },
    { value: 'showroom', label: t('showroom') }
  ];

  const beforeCitiesOptionsMaping = useCallback(
    (options: any) => {
      return options.filter((item: any) => (formik?.values?.delivery_type === 'home-delivery' ? item?.delivery_options?.length > 0 : item?.showrooms?.length > 0));
    },
    [formik?.values?.delivery_type]
  );

  const {
    modelOptions: citiesOptions,
    apiDataLoading: citiesApiDataLoading,
    apiData: citiesApiData
  } = useModelSelectOptions({
    labelKey: `name.${i18n.language}`,
    valueKey: 'id',
    modelRoute: 'admin/cities',
    queryKey: ['cities'],
    beforeOptionsMaping: beforeCitiesOptionsMaping
  });

  const { deliveryOptionsOptions, showroomsOptions } = useMemo(() => {
    const city = citiesApiData?.find((item: ICity) => item.id === formik?.values?.city_id);
    const deliveryOptionsOptions = (city?.delivery_options || [])?.map((item: any) => ({
      label: (
        <div className="f a-c s-b">
          <span>{item?.name?.[`${i18n.language}`]}</span>
          <span>{item?.price ? `${Number(item?.price)?.toLocaleString('en-US')} ${t('sar')}` : t('included')}</span>
        </div>
      ),
      value: item?.id
    }));
    const showroomsOptions = (city?.showrooms || [])?.map((item: any) => ({ label: item?.name?.[`${i18n.language}`], value: item?.id }));

    return {
      deliveryOptionsOptions,
      showroomsOptions
    };
  }, [formik?.values?.city_id, citiesApiData, i18n.language]);

  const tabs = useMemo(
    () => [
      {
        title: (
          <Fragment>
            <span className='f'>{homeDeliveryIcon}</span> <span>{t('home_delivery')}</span>
          </Fragment>
        ),
        value: 'home-delivery'
      },
      {
        title: (
          <Fragment>
            <span className='f'>{showroomIcon}</span> <span>{t('showroom')}</span>
          </Fragment>
        ),
        value: 'showroom'
      }
    ],
    [t]
  );

  return (
    <div className="field-array-container">
      <Accordion
        expanded={expanded == 3}
        onChange={(e) => {
          e.stopPropagation();
          setExpanded(expanded == 3 ? -1 : 3);
        }}
      >
        <AccordionSummary
          sx={{
            '&.Mui-focusVisible': {
              backgroundColor: '#000'
            }
          }}
          aria-controls="panel1a-content"
          id="panel1a-header"
          className={`header-container ${expanded != 3 && 'expand'}`}
        >
          <p>
            <ExpandAccordion expand={expanded == 3} /> {t('delivery_details')}
          </p>
        </AccordionSummary>
        <AccordionDetails>
          <div className="inputs_grid no-bottom-padding">
            <div className="grid-12">
              <h5 style={{ marginBottom: '1rem' }}>{t('delivery_type')}</h5>
              <Tabs
                tabs={tabs}
                value={formik.values.delivery_type}
                setValue={(value: string) => {
                  formik.setFieldValue('delivery_type', value);
                }}
                theme="secondary"
              />
            </div>
            {!formik?.values?.delivery_type ? null : citiesApiDataLoading ? (
              <InputSkeleton gridSize={6} />
            ) : (
              <FieldWrapper
                title={t('city')}
                inputName={`city_id`}
                inputPlaceholder={t('city')}
                options={citiesOptions}
                selectStyle={customStyles}
                select
                value={citiesOptions?.find((item: TOptions) => formik?.values?.city_id === item?.value)}
                gridSize={6}
              />
            )}
            {formik?.values?.delivery_type === 'home-delivery' ? (
              <Fragment>
                <FieldWrapper
                  title={t('delivery_option')}
                  inputName={`delivery_option_id`}
                  inputPlaceholder={t('delivery_option')}
                  options={deliveryOptionsOptions}
                  select
                  disabled={!formik?.values?.city_id}
                  value={deliveryOptionsOptions?.filter((item: TOptions) => formik?.values?.delivery_option_id === item?.value)}
                  selectStyle={customStyles}
                  gridSize={6}
                />
              </Fragment>
            ) : formik?.values?.delivery_type === 'showroom' ? (
              <Fragment>
                <FieldWrapper
                  title={t('showroom')}
                  inputName={`showroom_id`}
                  inputPlaceholder={t('showroom')}
                  options={showroomsOptions}
                  value={showroomsOptions?.filter((item: TOptions) => formik?.values?.showroom_id === item?.value)}
                  selectStyle={customStyles}
                  select
                  gridSize={6}
                />
              </Fragment>
            ) : null}
            {formik?.values?.delivery_type === 'home-delivery' && (
              <Fragment>
                <FieldWrapper title={t('area')} inputName={`area`} inputPlaceholder={t('area')} input gridSize={6} />
                <FieldWrapper title={t('street')} inputName={`street`} inputPlaceholder={t('street')} input gridSize={6} />
                <FieldWrapper title={t('building_no')} inputName={`building_no`} inputPlaceholder={t('building_no')} input type="number" gridSize={6} />
                <FieldWrapper title={t('apartment_no')} inputName={`apartment_no`} inputPlaceholder={t('apartment_no')} input type="number" gridSize={6} />
              </Fragment>
            )}
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
